import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "classes", "overrides", "className", "name", "dateFormat", "formatDate", "onChange", "value", "outputType", "lang", "isReadOnly", "isFullWidth", "calendarButtonLabel", "placeholder", "minDate", "maxDate", "customComponent"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Flatpickr from 'react-flatpickr';
import flatpickr from 'flatpickr';
import flatpickrl10n from 'flatpickr/dist/l10n';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Input from '../Input';
import Icon from '../../general/Icon';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
var useStyles = createUseStyles(styles, 'DatePicker');

// Old browser and Safari fix fix
(function () {
  if (document.getElementById('flatpickr')) return;
  var styleTag = document.createElement('style');
  styleTag.id = 'flatpickr';
  var headTag = document.getElementsByTagName('head')[0];
  headTag.insertBefore(styleTag, headTag.childNodes[0]);
})();
var _ref3 = /*#__PURE__*/React.createElement(Icon, {
  name: "calendar"
});
function DatePicker(_ref) {
  var children = _ref.children,
    classesProp = _ref.classes,
    overridesProp = _ref.overrides,
    classNameProp = _ref.className,
    name = _ref.name,
    dateFormat = _ref.dateFormat,
    formatDate = _ref.formatDate,
    onChange = _ref.onChange,
    value = _ref.value,
    outputType = _ref.outputType,
    lang = _ref.lang,
    isReadOnly = _ref.isReadOnly,
    isFullWidth = _ref.isFullWidth,
    calendarButtonLabel = _ref.calendarButtonLabel,
    placeholder = _ref.placeholder,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    customComponent = _ref.customComponent,
    props = _objectWithoutProperties(_ref, _excluded);
  var flatpickrRef = useRef();
  var todayButtonRef = useRef();
  var classes = useClasses(useStyles, classesProp);

  // Overrides
  var override = getOverrides(overridesProp, DatePicker.overrides);

  // Classes
  var rootClassName = classnames(classes.root, _defineProperty({}, classes.isFullWidth, isFullWidth), classNameProp);
  var flatpickrOptions = useMemo(function () {
    return _objectSpread({
      dateFormat: dateFormat || 'd M Y',
      formatDate: formatDate,
      locale: flatpickrl10n[lang],
      clickOpens: !isReadOnly,
      time_24hr: true,
      minDate: minDate,
      maxDate: maxDate,
      disableMobile: true
    }, override.flatpickrOptions);
  }, [dateFormat, formatDate, isReadOnly, lang, maxDate, minDate, override.flatpickrOptions]);
  var onChangeDate = useCallback(function (date) {
    if (isReadOnly) return;
    var formattedDate = outputType === 'string' ? flatpickr.formatDate(date[0], flatpickrOptions.dateFormat) : date[0];
    onChange && onChange(formattedDate, name);
  }, [flatpickrOptions.dateFormat, isReadOnly, name, onChange, outputType]);

  // Remove handler
  var onInputChange = useCallback(function () {
    if (isReadOnly) return;
    onChange && onChange(undefined, name);
  }, [isReadOnly, name, onChange]);
  var shouldDisableToday = useMemo(function () {
    if (minDate || maxDate) {
      var today = new Date();
      if (minDate && minDate.getTime() > today.getTime()) return true;
      if (maxDate && maxDate.getTime() < today.getTime()) return true;
    }
    return false;
  }, [minDate, maxDate]);
  var todayClicked = useCallback(function () {
    if (shouldDisableToday) return;
    var today = new Date(new Date().setHours(0, 0, 0, 0));
    onChangeDate([today]);
    flatpickrRef.current.flatpickr.close();
  }, [onChangeDate, shouldDisableToday]);
  var onReady = useCallback(function (_, __, fp) {
    fp.calendarContainer.classList.add(classes.container);
    if (fp.rContainer) {
      var div = document.createElement('div');
      var classNames = [classes.todayContainer];
      if (shouldDisableToday) classNames.push(classes.todayContainerDisabled);
      div.className = classNames.join(' ');
      div.addEventListener('click', todayClicked);
      div.innerHTML = calendarButtonLabel;
      todayButtonRef.current = {
        element: div,
        function: todayClicked
      };
      fp.rContainer.appendChild(div);
    }
  }, [classes.container, classes.todayContainer, classes.todayContainerDisabled, shouldDisableToday, todayClicked, calendarButtonLabel]);
  useEffect(function () {
    if (todayButtonRef.current) {
      todayButtonRef.current.element.removeEventListener('click', todayButtonRef.current.function);
      todayButtonRef.current.element.addEventListener('click', todayClicked);
      todayButtonRef.current.function = todayClicked;
    }
  }, [classes.todayContainer, classes.todayContainerDisabled, todayClicked]);
  useEffect(function () {
    if (todayButtonRef.current) {
      var classNames = [classes.todayContainer];
      if (shouldDisableToday) classNames.push(classes.todayContainerDisabled);
      todayButtonRef.current.element.className = classNames.join(' ');
    }
  }, [classes.todayContainer, classes.todayContainerDisabled, shouldDisableToday]);
  var onClick = useCallback(function (e) {
    setTimeout(function () {
      return flatpickrRef.current.flatpickr.open();
    });
  }, []);
  var inputOverride = useMemo(function () {
    return {
      formControl: {
        onClick: !isReadOnly ? onClick : undefined
      }
    };
  }, [isReadOnly, onClick]);
  var flatpickrRender = useCallback(function (_ref2, ref) {
    var className = _ref2.className,
      value = _ref2.value;
    var formatValue = value && outputType === 'object' ? formatDate ? formatDate(value, flatpickrOptions.dateFormat) : flatpickr.formatDate(value, flatpickrOptions.dateFormat) : value;
    if (customComponent) {
      var CustomComponent = customComponent;
      return /*#__PURE__*/React.createElement(CustomComponent, _extends({}, props, {
        inputRef: ref,
        value: formatValue,
        className: className,
        onClick: onClick
      }));
    }
    return /*#__PURE__*/React.createElement(Input, _extends({}, props, {
      value: formatValue,
      className: className,
      isReadOnly: isReadOnly,
      isFullWidth: isFullWidth,
      onChange: onInputChange,
      overrides: _objectSpread({
        input: {
          ref: ref
        }
      }, inputOverride),
      placeholder: placeholder,
      postComponent: !isReadOnly && /*#__PURE__*/React.createElement("div", {
        className: classes.calendarIcon,
        onClick: onClick
      }, _ref3)
    }));
  }, [classes.calendarIcon, flatpickrOptions.dateFormat, formatDate, inputOverride, isFullWidth, isReadOnly, onClick, onInputChange, outputType, placeholder, props, customComponent]);
  var key = "flatpickr-".concat(name || 'anon', "--").concat(isReadOnly ? 'read-only' : 'active');
  return /*#__PURE__*/React.createElement(Flatpickr, _extends({
    className: rootClassName,
    ref: flatpickrRef,
    key: key,
    options: flatpickrOptions,
    render: flatpickrRender,
    overrides: overridesProp,
    value: value,
    onReady: onReady,
    onChange: onChangeDate
  }, override.flatpickr));
}
DatePicker.overrides = ['root', 'input', 'flatpickr', 'flatpickrOptions'];
DatePicker.defaultProps = {
  labelMode: 'vertical',
  outputType: 'object',
  onChange: function onChange() {},
  value: '',
  dateFormat: '',
  lang: 'en',
  isReadOnly: false,
  calendarButtonLabel: 'Today',
  placeholder: 'Select date',
  override: {}
};
process.env.NODE_ENV !== "production" ? DatePicker.propTypes = {
  className: PropTypes.string,
  overrides: PropTypes.object,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onEnter: PropTypes.func,
  /** Native input id */
  id: PropTypes.string,
  /** Native input name */
  name: PropTypes.string,
  value: PropTypes.any,
  label: PropTypes.string,
  labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
  isFullWidth: PropTypes.bool,
  /** Info popover */
  hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Error will be displayed below the component with style changes */
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Info will be displayed below the component with style changes */
  info: PropTypes.string,
  isRequired: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  outputType: PropTypes.oneOf(['object', 'string']),
  placeholder: PropTypes.string,
  lang: PropTypes.string,
  /** default to flatpickr format tokens */
  dateFormat: PropTypes.string,
  /** Custom formatDate function */
  formatDate: PropTypes.func,
  /** Custom text for Today's button */
  calendarButtonLabel: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  customComponent: PropTypes.elementType
} : void 0;
export default /*#__PURE__*/React.memo(DatePicker);